import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collections } from "@/mocks/categories";
import { dramas } from "@/mocks/dramas";
import { sortDramas } from "@/lib/data";
import type { Drama } from "@/types";
import PageHeader from "@/components/base/PageHeader";
import DramaListItem from "@/components/base/DramaListItem";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 5;

function buildCollection(id: string): Drama[] {
  const all = dramas as Drama[];
  switch (id) {
    case "top-rated":
      return sortDramas(all, "rating");
    case "new-release":
      return sortDramas(
        all.filter((d) => d.year >= 2024),
        "updated",
      );
    case "completed":
      return sortDramas(
        all.filter((d) => d.status.includes("已完结")),
        "popularity",
      );
    case "hot":
    default:
      return sortDramas(all, "popularity");
  }
}

export default function DramaList() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const collection = collections.find((c) => c.id === categoryId);
  const [status, setStatus] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta(
    `${collection ? collection.name : "剧集列表"} - 漂亮的保姆`,
    `${collection ? collection.desc : "影视剧专题列表。"}在漂亮的保姆浏览完整剧集列表，查看主演、评分、更新状态与剧情简介。`,
    `漂亮的保姆,${collection ? collection.name : "剧集列表"},电视剧列表,影视剧资讯,剧情介绍`,
  );

  const list = useMemo(() => buildCollection(categoryId || "hot"), [categoryId]);

  const filtered = useMemo(() => {
    if (!status) return list;
    if (status === "completed") return list.filter((d) => d.status.includes("已完结"));
    return list.filter((d) => d.status.includes("更新"));
  }, [list, status]);

  const shown = filtered.slice(0, visible);

  if (!collection) {
    return (
      <>
        <PageHeader
          title="列表不存在"
          crumbs={[{ label: "首页", to: "/" }, { label: "剧集列表" }]}
        />
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10">
          <EmptyState
            title="未找到该列表"
            desc="你访问的剧集列表不存在，可以浏览热播榜或其他列表。"
            actionText="查看热播榜"
            onAction={() => window.REACT_APP_NAVIGATE("/list/hot")}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={collection.name}
        desc={collection.desc}
        crumbs={[{ label: "首页", to: "/" }, { label: "剧集列表" }, { label: collection.name }]}
      />

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "", label: "全部" },
              { key: "updating", label: "连载中" },
              { key: "completed", label: "已完结" },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  setStatus(s.key);
                  setVisible(PAGE_SIZE);
                }}
                className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                  status === s.key
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-foreground-500">
            共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部作品
          </p>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((d) => (
                <DramaListItem key={d.id} drama={d} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="text-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-primary-300 text-primary-700 bg-background-50 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  加载更多（剩余 {filtered.length - visible} 部）
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title="暂无符合条件的剧集"
            desc="试试切换更新状态筛选，查看其他作品。"
            actionText="查看全部"
            onAction={() => setStatus("")}
          />
        )}
      </div>
    </>
  );
}